'use client'

import React, { Suspense, useEffect, useRef } from 'react'

import { useQuery } from '@tanstack/react-query'

import {
  useRecipientAnalyticsQueries,
  useSuspenseRecipientDashboard,
} from '@/hooks/usaspending'

import type { CategoryBreakdown } from '@/types/usaspending'

import { AwardMixDonut } from '../../charts/AwardMixDonut'
import { QueryErrorBoundary } from '../../DataErrorBoundary'
import { SectionCard, SectionSkeleton, useDetailFilter } from '../../shared'
import { DonutUnavailable } from '../../shared/ServiceUnavailable'
import { useRecipientDetail } from '../context'

/**
 * RecipientDetail.AwardMix
 *
 * Donut chart showing award type breakdown (contract, grant, IDV, loan, etc.)
 * with interactive legend and center total.
 */
export function AwardMixSection() {
  const { recipientId } = useRecipientDetail()

  return (
    <SectionCard id="award-mix" title="Award Mix" animationDelay={50}>
      <QueryErrorBoundary
        title="Failed to load award types"
        serviceUnavailableFallback={<DonutUnavailable height={200} />}
      >
        <Suspense fallback={<SectionSkeleton height={200} />}>
          <AwardMixContent id={recipientId} />
        </Suspense>
      </QueryErrorBoundary>
    </SectionCard>
  )
}

function AwardMixContent({ id }: { id: string }) {
  const { state } = useDetailFilter()
  const fiscalYear = state.fiscalYear === 'all' ? undefined : state.fiscalYear
  const { data: dashboard } = useSuspenseRecipientDashboard(id, fiscalYear)

  if (dashboard.awardTypes === null) {
    return <DeferredAwardMix id={id} fiscalYear={fiscalYear} />
  }

  return <AwardMixInner data={dashboard.awardTypes} />
}

function DeferredAwardMix({
  id,
  fiscalYear,
}: {
  id: string
  fiscalYear?: number
}) {
  const queries = useRecipientAnalyticsQueries()
  const { data, isLoading } = useQuery(queries.awardTypes(id, fiscalYear))

  if (isLoading) return <SectionSkeleton height={200} />
  if (!data) return null

  return <AwardMixInner data={data} />
}

function AwardMixInner({ data }: { data: CategoryBreakdown[] }) {
  const chartRef = useRef<HTMLDivElement>(null)
  const { registerChartRef } = useRecipientDetail()

  // Register chart ref for PDF export capture
  useEffect(() => {
    const element = chartRef.current
    if (element) {
      registerChartRef('awardMix', element)
    }
    return () => {
      registerChartRef('awardMix', null)
    }
  }, [registerChartRef])

  return (
    <div ref={chartRef}>
      <AwardMixDonut data={data} showLegend />
    </div>
  )
}
